import { useState, ReactNode } from "react";
import { message } from "antd";

import {
  ChatBubbleBottomCenterTextIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  EnvelopeIcon,
  PhoneArrowUpRightIcon,
} from "@heroicons/react/24/outline";
import { FaInstagram } from "react-icons/fa6";
import { RiMessengerLine, RiWhatsappLine } from "react-icons/ri";

import { Field, Label, Description } from "V2.0/common/fieldset";
import { Listbox, ListboxOption, ListboxLabel } from "V2.0/common/listbox";

import { getAssistant } from "V2.0/HomePages/Assistants/service";
import { showErrorToast } from "V2.0/utils";

import { defaultPayloadMapping } from "../../../Models/WebhookTrigger/payloadMapping";

export default AgentAutofill;

const AUTOFILL_MESSAGE_KEY = "webhook-payload-autofill";

// Standard fields each channel posts to the webhook, added by default when an
// agent of that type is selected (in addition to its extraction fields).
const DEFAULT_PAYLOAD_FIELDS_BY_TYPE: Record<string, string[]> = {
  voice: [
    "conversation",
    "conversationId",
    "summary",
    "type",
    "phone",
    "assistantPhone",
    "callDirection",
    "assistantId",
    "callDuration",
    "callStartTime",
    "callEndTime",
    "callId",
    "callEndReason",
    "telephonyCallId",
    "callRecordingUrl",
  ],
  whatsapp: [
    "conversation",
    "conversationId",
    "summary",
    "phone",
    "conversationStartTime",
  ],
  instagram: ["conversation", "conversationId", "conversationStartTime"],
  chat: ["conversation", "conversationId", "conversationStartTime"],
  messenger: ["conversation", "conversationId", "conversationStartTime"],
  sms: []
};

function buildDefaultMappings(fields: string[]) {
  return fields.map((field) => {
    // "phone" is the contact identifier the webhook needs; map it to the
    // contact field. Everything else lands as a pipeline attribute.
    if (field === "phone") {
      return {
        ...defaultPayloadMapping,
        sourceField: "phone",
        targetField: "phone",
        targetType: "contactField",
      };
    }

    return {
      ...defaultPayloadMapping,
      sourceField: field,
      targetField: field,
    };
  });
}

const DEFAULT_PAYLOAD_MAPPINGS_BY_TYPE: Record<
  string,
  ReturnType<typeof buildDefaultMappings>
> = Object.fromEntries(
  Object.entries(DEFAULT_PAYLOAD_FIELDS_BY_TYPE).map(([type, fields]) => [
    type,
    buildDefaultMappings(fields),
  ]),
);

// Small inline icon per agent type, shown before the agent name in the dropdown.
const AGENT_TYPE_ICONS: Record<string, ReactNode> = {
  voice: <PhoneArrowUpRightIcon className="size-4" />,
  sms: <ChatBubbleBottomCenterTextIcon className="size-4" />,
  whatsapp: <RiWhatsappLine className="size-4" />,
  messenger: <RiMessengerLine className="size-4" />,
  instagram: <FaInstagram className="size-4" />,
  chat: <ChatBubbleOvalLeftEllipsisIcon className="size-4" />,
  email: <EnvelopeIcon className="size-4" />,
};

// Extraction lives at assistant.extraction after flattening, or under
// assistantSettings.assistantTools.extraction on the raw detail response.
function getExtractionParameters(assistant) {
  return (
    assistant?.extraction?.extractionParameters ??
    assistant?.assistantSettings?.assistantTools?.extraction
      ?.extractionParameters ??
    []
  );
}

function AgentAutofill(props) {
  const { assistants = [], workspaceId, onAutofill } = props;

  const [selectedAssistantId, setSelectedAssistantId] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSelectAssistant = async (assistantId) => {
    setSelectedAssistantId(assistantId);

    if (!assistantId) return;

    setLoading(true);
    message.loading({
      content: "Loading extraction fields",
      key: AUTOFILL_MESSAGE_KEY,
    });

    try {
      const response = await getAssistant({ workspaceId, assistantId });
      const extractionParameters = getExtractionParameters(response.data);

      const assistantType = assistants.find(
        (assistant: any) => assistant._id === assistantId,
      )?.assistantType;

      const defaultMappings =
        DEFAULT_PAYLOAD_MAPPINGS_BY_TYPE[assistantType] || [];
      const defaultSourceFields = new Set(
        defaultMappings.map((mapping) => mapping.sourceField),
      );

      // Extraction fields come after the defaults; skip any that a default
      // already covers to avoid duplicate source fields.
      const extractionMappings = extractionParameters
        .filter((parameter) => parameter?.parameterName)
        .filter((parameter) => !defaultSourceFields.has(parameter.parameterName))
        .map((parameter) => ({
          ...defaultPayloadMapping,
          sourceField: parameter.parameterName,
          targetField: parameter.parameterName,
        }));

      const mappings = [...defaultMappings, ...extractionMappings];

      if (mappings.length === 0) {
        message.info({
          content: "This agent has no fields to map",
          key: AUTOFILL_MESSAGE_KEY,
        });
        return;
      }

      onAutofill(mappings);

      message.success({
        content: `Mapped ${mappings.length} field${
          mappings.length > 1 ? "s" : ""
        } from the extraction schema`,
        key: AUTOFILL_MESSAGE_KEY,
      });
    } catch (e) {
      message.destroy(AUTOFILL_MESSAGE_KEY);
      showErrorToast(e, "Failed to load extraction fields");
    } finally {
      setLoading(false);
    }
  };

  if (assistants.length === 0) return null;

  return (
    <Field>
      <Label>Auto-fill from Agent</Label>
      <Description>
        Select an agent to fill the mapped fields from its extraction schema.
        This replaces the current fields.
      </Description>
      <Listbox
        as="div"
        className="mt-2"
        value={selectedAssistantId}
        onChange={handleSelectAssistant}
        disabled={loading}
        placeholder="Select an agent"
      >
        <ListboxOption value="">
          <ListboxLabel>Select an agent</ListboxLabel>
        </ListboxOption>
        {assistants.map((assistant) => (
          <ListboxOption
            key={assistant._id}
            value={assistant._id}
          >
            {AGENT_TYPE_ICONS[assistant.assistantType]}
            <ListboxLabel>{assistant.name}</ListboxLabel>
          </ListboxOption>
        ))}
      </Listbox>
    </Field>
  );
}
